// src/stores/riskMatrixStore.ts
import { proxy, subscribe } from 'valtio';
import { debounce } from 'lodash';
import { api } from '../utils/api';

export type RAGColor = 'green' | 'amber' | 'red';

export interface RiskMatrixConfig {
  infoSec: {
    colors: RAGColor[][];
    likelihoodLabels: Array<{ label: string; description: string }>;
    impactLabels: Array<{ label: string; description: string }>;
    likelihoodHeading: string;
    impactHeading: string;
  };
  nonInfoSec: {
    colors: RAGColor[][];
    likelihoodLabels: Array<{ label: string; description: string }>;
    severityLabels: Array<{ label: string; description: string }>;
    likelihoodHeading: string;
    severityHeading: string;
  };
}

// Default configurations (used as fallback)
const defaultInfoSecColors: RAGColor[][] = [
  ['green', 'green', 'green', 'green', 'amber'],
  ['green', 'green', 'amber', 'amber', 'amber'],
  ['green', 'amber', 'amber', 'amber', 'red'],
  ['green', 'amber', 'amber', 'red', 'red'],
  ['amber', 'amber', 'red', 'red', 'red'],
];

const defaultNonInfoSecColors: RAGColor[][] = [
  ['green', 'green', 'amber', 'amber', 'amber'],
  ['green', 'green', 'amber', 'amber', 'red'],
  ['green', 'amber', 'amber', 'amber', 'red'],
  ['green', 'amber', 'amber', 'red', 'red'],
  ['green', 'amber', 'red', 'red', 'red'],
];

const defaultInfoSecLikelihood = [
  { label: 'Rare', description: 'May only occur in exceptional circumstances (less than once in 10 years)' },
  { label: 'Unlikely', description: 'Could occur at some time but not expected (once in 5-10 years)' },
  { label: 'Possible', description: 'Might occur at some time (once in 2-5 years)' },
  { label: 'Likely', description: 'Will probably occur in most circumstances (once per year)' },
  { label: 'Almost Certain', description: 'Expected to occur frequently (multiple times per year)' },
];

const defaultInfoSecImpact = [
  { label: 'Insignificant', description: 'Minimal impact on operations, no financial loss, no reputational damage' },
  { label: 'Minor', description: 'Minor impact on operations, small financial loss, limited reputational damage' },
  { label: 'Moderate', description: 'Moderate disruption to operations, moderate financial loss, some reputational damage' },
  { label: 'Serious', description: 'Major disruption to operations, significant financial loss, serious reputational damage' },
  { label: 'Catastrophic', description: 'Critical impact on operations, severe financial loss, catastrophic reputational damage' },
];

const defaultNonInfoSecLikelihood = [
  { label: 'Rare', description: 'May only occur in exceptional circumstances (less than once in 10 years)' },
  { label: 'Unlikely', description: 'Could occur at some time but not expected (once in 5-10 years)' },
  { label: 'Possible', description: 'Might occur at some time (once in 2-5 years)' },
  { label: 'Likely', description: 'Will probably occur in most circumstances (once per year)' },
  { label: 'Almost Certain', description: 'Expected to occur frequently (multiple times per year)' },
];

const defaultNonInfoSecSeverity = [
  { label: 'Insignificant', description: 'Minimal impact on operations, no financial loss, no reputational damage' },
  { label: 'Minor', description: 'Minor impact on operations, small financial loss, limited reputational damage' },
  { label: 'Moderate', description: 'Moderate disruption to operations, moderate financial loss, some reputational damage' },
  { label: 'Serious', description: 'Major disruption to operations, significant financial loss, serious reputational damage' },
  { label: 'Catastrophic', description: 'Critical impact on operations, severe financial loss, catastrophic reputational damage' },
];

const getDefaults = (): RiskMatrixConfig => ({
  infoSec: {
    colors: defaultInfoSecColors.map(row => [...row]),
    likelihoodLabels: defaultInfoSecLikelihood.map(l => ({ ...l })),
    impactLabels: defaultInfoSecImpact.map(i => ({ ...i })),
    likelihoodHeading: 'Likelihood Scale',
    impactHeading: 'Severity Scale',
  },
  nonInfoSec: {
    colors: defaultNonInfoSecColors.map(row => [...row]),
    likelihoodLabels: defaultNonInfoSecLikelihood.map(l => ({ ...l })),
    severityLabels: defaultNonInfoSecSeverity.map(s => ({ ...s })),
    likelihoodHeading: 'Likelihood Scale',
    severityHeading: 'Severity Scale',
  },
});

// Store - initialise with defaults, then load from API
export const riskMatrixStore = proxy<RiskMatrixConfig>(getDefaults());

// Track last-known server timestamp outside reactive proxy to avoid save loops
let riskMatrixLastSavedAt = '';

// Guard: suppress auto-saves while loading from API to prevent writing defaults back
let riskMatrixLoading = false;

// Debounced save to API with conflict detection (auto-saves on every store mutation)
const debouncedSaveRiskMatrix = debounce(async () => {
  if (riskMatrixLoading) return; // Don't save while loading — store may contain defaults
  try {
    const payload = {
      ...JSON.parse(JSON.stringify({
        infoSec: riskMatrixStore.infoSec,
        nonInfoSec: riskMatrixStore.nonInfoSec,
      })),
      _updatedAt: riskMatrixLastSavedAt,
    };
    const response = await api.put('/risk-matrix', payload) as Record<string, unknown>;
    if (response._updatedAt !== undefined) {
      riskMatrixLastSavedAt = (response._updatedAt as string) || '';
    }
  } catch (e: unknown) {
    const err = e as Error & { status?: number };
    if (err.status === 409) {
      console.warn('Risk matrix config conflict detected, reloading...');
      await riskMatrixActions.loadConfig();
    } else {
      console.error('Failed to save risk matrix config to API:', e);
    }
  }
}, 500);

// Actions
export const riskMatrixActions = {
  loadConfig: async () => {
    riskMatrixLoading = true;
    try {
      const data = await api.get('/risk-matrix') as { infoSec?: object; nonInfoSec?: object; _updatedAt?: string } | null;
      if (data) {
        // Extract _updatedAt (use !== undefined so empty string '' is still handled)
        if (data._updatedAt !== undefined) {
          riskMatrixLastSavedAt = data._updatedAt || '';
          delete data._updatedAt;
        }
        if (data.infoSec) Object.assign(riskMatrixStore.infoSec, data.infoSec);
        if (data.nonInfoSec) Object.assign(riskMatrixStore.nonInfoSec, data.nonInfoSec);
      } else {
        // No config saved yet — persist defaults so they survive reloads
        riskMatrixLoading = false;
        debouncedSaveRiskMatrix();
        return;
      }
    } catch (e) {
      console.error('Failed to load risk matrix config from API:', e);
      // Fallback to localStorage
      const stored = localStorage.getItem('riskMatrixConfig');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          Object.assign(riskMatrixStore.infoSec, parsed.infoSec);
          Object.assign(riskMatrixStore.nonInfoSec, parsed.nonInfoSec);
        } catch {}
      }
    } finally {
      riskMatrixLoading = false;
    }
  },

  saveConfig: async () => {
    try {
      const payload = {
        ...JSON.parse(JSON.stringify({
          infoSec: riskMatrixStore.infoSec,
          nonInfoSec: riskMatrixStore.nonInfoSec,
        })),
        _updatedAt: riskMatrixLastSavedAt,
      };
      const response = await api.put('/risk-matrix', payload) as Record<string, unknown>;
      if (response._updatedAt !== undefined) {
        riskMatrixLastSavedAt = (response._updatedAt as string) || '';
      }
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 409) {
        console.warn('Risk matrix config conflict detected, reloading...');
        await riskMatrixActions.loadConfig();
      } else {
        console.error('Failed to save risk matrix config to API:', e);
      }
    }
  },

  updateInfoSecColor: (likelihoodIdx: number, impactIdx: number, color: RAGColor) => {
    riskMatrixStore.infoSec.colors[likelihoodIdx][impactIdx] = color;
  },
  updateNonInfoSecColor: (likelihoodIdx: number, severityIdx: number, color: RAGColor) => {
    riskMatrixStore.nonInfoSec.colors[likelihoodIdx][severityIdx] = color;
  },
  updateInfoSecLikelihoodDesc: (index: number, description: string) => { riskMatrixStore.infoSec.likelihoodLabels[index].description = description; },
  updateInfoSecLikelihoodLabel: (index: number, label: string) => { riskMatrixStore.infoSec.likelihoodLabels[index].label = label; },
  updateInfoSecImpactDesc: (index: number, description: string) => { riskMatrixStore.infoSec.impactLabels[index].description = description; },
  updateInfoSecImpactLabel: (index: number, label: string) => { riskMatrixStore.infoSec.impactLabels[index].label = label; },
  updateNonInfoSecLikelihoodDesc: (index: number, description: string) => { riskMatrixStore.nonInfoSec.likelihoodLabels[index].description = description; },
  updateNonInfoSecLikelihoodLabel: (index: number, label: string) => { riskMatrixStore.nonInfoSec.likelihoodLabels[index].label = label; },
  updateNonInfoSecSeverityDesc: (index: number, description: string) => { riskMatrixStore.nonInfoSec.severityLabels[index].description = description; },
  updateNonInfoSecSeverityLabel: (index: number, label: string) => { riskMatrixStore.nonInfoSec.severityLabels[index].label = label; },
  updateInfoSecLikelihoodHeading: (heading: string) => { riskMatrixStore.infoSec.likelihoodHeading = heading; },
  updateInfoSecImpactHeading: (heading: string) => { riskMatrixStore.infoSec.impactHeading = heading; },
  updateNonInfoSecLikelihoodHeading: (heading: string) => { riskMatrixStore.nonInfoSec.likelihoodHeading = heading; },
  updateNonInfoSecSeverityHeading: (heading: string) => { riskMatrixStore.nonInfoSec.severityHeading = heading; },

  resetToDefaults: () => {
    const defaults = getDefaults();
    Object.assign(riskMatrixStore.infoSec, defaults.infoSec);
    Object.assign(riskMatrixStore.nonInfoSec, defaults.nonInfoSec);
    // subscribe handler will auto-save
  },

  getColorClass: (color: RAGColor): string => {
    switch (color) {
      case 'green': return 'bg-green-500';
      case 'amber': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
    }
  },
};

// Auto-save on any store mutation (debounced)
subscribe(riskMatrixStore, debouncedSaveRiskMatrix);
